import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { formatAbsoluteDateTime } from "@/lib/format";
import type { Source, SyncStatus } from "@finance/shared";

const STALE_THRESHOLD_MS = 24 * 60 * 60 * 1000;

interface SyncStatusPillProps {
  source: Source;
  lastSyncedAt: string | null | undefined;
  lastSyncStatus: SyncStatus | null | undefined;
  className?: string;
  now?: Date;
  // When true, render an in-progress "Syncing…" badge overriding the
  // derived state. Used by the /accounts page while a Plaid sync request
  // is in flight (FIN-32).
  syncing?: boolean;
}

type State = "synced" | "stale" | "failed" | "pending" | "syncing";

export function deriveSyncState(args: {
  source: Source;
  lastSyncedAt: string | null | undefined;
  lastSyncStatus: SyncStatus | null | undefined;
  now?: Date;
}): State | null {
  if (args.source !== "api") return null;
  if (args.lastSyncStatus === "failed") return "failed";
  if (!args.lastSyncedAt) return "pending";
  const ts = Date.parse(args.lastSyncedAt);
  if (Number.isNaN(ts)) return "pending";
  const now = args.now ?? new Date();
  return now.getTime() - ts > STALE_THRESHOLD_MS ? "stale" : "synced";
}

export function SyncStatusPill({
  source,
  lastSyncedAt,
  lastSyncStatus,
  className,
  now,
  syncing,
}: SyncStatusPillProps) {
  if (source !== "api") return null;
  const state: State =
    syncing
      ? "syncing"
      : (deriveSyncState({ source, lastSyncedAt, lastSyncStatus, now }) ??
          "pending");

  const tooltip = lastSyncedAt
    ? `Last synced ${formatAbsoluteDateTime(lastSyncedAt)}`
    : state === "failed"
      ? "Last sync attempt failed"
      : "Awaiting first sync";

  if (state === "syncing") {
    return (
      <Badge
        variant="outline"
        className={cn(
          "font-mono text-[10px] uppercase tracking-wide text-muted-foreground",
          className,
        )}
        title="Sync in progress"
      >
        Syncing…
      </Badge>
    );
  }
  if (state === "synced") {
    return (
      <Badge
        variant="outline"
        className={cn(
          "border-success/30 bg-success/10 text-success font-mono text-[10px] uppercase tracking-wide",
          className,
        )}
        title={tooltip}
      >
        Synced
      </Badge>
    );
  }
  if (state === "stale") {
    return (
      <Badge
        variant="outline"
        className={cn(
          "border-warning/30 bg-warning/10 text-warning font-mono text-[10px] uppercase tracking-wide",
          className,
        )}
        title={`${tooltip} — older than 24h`}
      >
        Stale
      </Badge>
    );
  }
  if (state === "failed") {
    return (
      <Badge
        variant="outline"
        className={cn(
          "border-destructive/30 bg-destructive/10 text-destructive font-mono text-[10px] uppercase tracking-wide",
          className,
        )}
        title={tooltip}
      >
        Sync failed
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className={cn(
        "font-mono text-[10px] uppercase tracking-wide text-muted-foreground",
        className,
      )}
      title={tooltip}
    >
      Pending sync
    </Badge>
  );
}
